import { Button } from '@shadcn/ui/components/ui/button';
import { Textarea } from '@shadcn/ui/components/ui/textarea';
import { Input } from '@shadcn/ui/components/ui/input';
import { PopoverContent } from '@shadcn/ui/components/ui/popover';
import { FC, useState, useRef, useEffect, useCallback } from 'react';
import { useNoteMutations } from '../../hooks/messaging/use-note-mutations';
import { useToast } from '@shadcn/ui/hooks/use-toast';
import { cn } from '@shadcn/ui/lib/utils';

interface QuickAddNotePopoverProps {
    contactId: string;
    onClose: () => void;
    onNoteAdded?: () => void;
}

export const QuickAddNotePopover: FC<QuickAddNotePopoverProps> = ({ contactId, onClose, onNoteAdded }) => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { addNote } = useNoteMutations({ contactId });
    const { toast } = useToast();
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = '0px';
            const scrollHeight = textareaRef.current.scrollHeight;
            textareaRef.current.style.height = Math.min(scrollHeight, 200) + 'px';
        }
    }, [content]);

    // Focus on textarea when opened
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        if (!content.trim()) return;

        setIsLoading(true);
        try {
            await addNote({
                content: content.trim(),
                title: title.trim() || undefined,
            });

            toast({
                title: 'Note added successfully',
                variant: 'default',
            });

            setTitle('');
            setContent('');
            onNoteAdded?.();
            onClose();
        } catch (error) {
            console.error('Error adding note:', error);
            toast({
                title: 'Failed to add note',
                description: 'Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [content, title, addNote, toast, onNoteAdded, onClose]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                handleSubmit();
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
            }
        },
        [handleSubmit, onClose]
    );

    return (
        <PopoverContent
            className="w-80 p-0"
            align="end"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div
                className="border-b rounded-t-lg"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="p-4 border-b">
                    <h3 className="font-medium text-sm">Add a note</h3>
                    <p className="text-xs text-text-tertiary mt-1">
                        Save information for yourself and the agent to keep track and write better messages
                    </p>
                </div>
                <div className="p-4 space-y-3">
                    <Textarea
                        ref={textareaRef}
                        placeholder="Add your note here..."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isLoading}
                        className={cn(
                            'min-h-[80px] max-h-[200px] resize-none text-sm bg-background-note',
                            'focus-visible:ring-1 focus-visible:ring-ring'
                        )}
                    />
                    <div className="flex justify-end items-center">
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={onClose} disabled={isLoading}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={handleSubmit} disabled={isLoading || !content.trim()}>
                                {isLoading ? 'Adding...' : 'Add note'}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </PopoverContent>
    );
};
